import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import type { NotificationBadgeProps } from '@magicschool/spellbook/components/NotificationBadge';
import { NotificationBadge } from '@magicschool/spellbook/components/NotificationBadge';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { SpinnerProps } from '@magicschool/spellbook/components/Spinner';
import { Spinner } from '@magicschool/spellbook/components/Spinner';
import { getControlIconSize } from '@magicschool/spellbook/lib/controls';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import type { IconButtonProps } from './IconButton';

const notificationBadgePositionVariants = cva('absolute', {
  variants: {
    buttonSize: {
      xs: '',
      sm: '',
      md: '',
      lg: '',
    },
    badgeSize: {
      xxs: '',
      xs: '',
    },
  },
  compoundVariants: [
    // xs & sm buttons use xxs badge
    { buttonSize: ['xs', 'sm'], badgeSize: 'xxs', className: '-right-0.5 -top-0.5' },
    // md button uses xxs badge
    { buttonSize: 'md', badgeSize: 'xxs', className: 'right-1 top-1' },
    // lg button uses xs badge
    { buttonSize: 'lg', badgeSize: 'xs', className: 'right-0.25 top-0.25' },
  ],
  defaultVariants: {
    buttonSize: 'md',
    badgeSize: 'xxs',
  },
});

const getNotificationBadgeSize = (buttonSize?: IconButtonProps['size']): 'xxs' | 'xs' => {
  switch (buttonSize) {
    case 'lg':
      return 'xs';
    default:
      return 'xxs';
  }
};

export const IconButtonSpinnerSlot = Slot<SpinnerProps>({
  displayName: 'IconButton.Spinner',
  isRenderedByDefault: true,
  renderDefault: ({ size: providedSize, ...props }) => {
    const size = getControlIconSize(providedSize);
    return <Spinner data-spellbook-name="icon-button-spinner" size={size} {...props} />;
  },
});

export const IconButtonIconSlot = Slot<IconProps>({
  displayName: 'IconButton.Icon',
  renderDefault: ({ size: providedSize, ...props }) => {
    const size = getControlIconSize(providedSize);
    return <Icon data-spellbook-name="icon-button-icon" size={size} {...props} />;
  },
});

export const IconButtonToggleIconSlot = Slot<IconProps>({
  displayName: 'IconButton.ToggleIcon',
  preventOverride: true,
  renderDefault: ({ name = 'chevron-down', className, size: providedSize, ...props }) => {
    const size = getControlIconSize(providedSize);
    return (
      <Icon
        data-spellbook-name="icon-button-toggle-icon"
        name={name}
        size={size}
        aria-hidden="true"
        role="presentation"
        className={cn('transform', 'group-data-[state=open]/button:rotate-180', 'group-aria-expanded/button:rotate-180', className)}
        {...props}
      />
    );
  },
});

export type IconButtonNotificationBadgeSlotProps = Omit<NotificationBadgeProps, 'size'> & {
  buttonSize?: IconButtonProps['size'];
};

export const IconButtonNotificationBadgeSlot = Slot<IconButtonNotificationBadgeSlotProps>({
  displayName: 'IconButton.NotificationBadge',
  preventOverride: true,
  renderDefault: ({ buttonSize, className, ...props }) => {
    const badgeSize = getNotificationBadgeSize(buttonSize);
    return (
      <NotificationBadge
        data-spellbook-name="icon-button-notification-badge"
        size={badgeSize}
        className={cn(notificationBadgePositionVariants({ buttonSize, badgeSize }), className)}
        {...props}
      />
    );
  },
});
